/**
 * The Joining card's two identifiers, side by side: the secret **Invite
 * code** with `Copy invite code` and `Generate a new invite code` behind the
 * shared named confirm, and the public **Pool ID** read-only beside it.
 * `issue.md` R3, R4, R5 and R7. GLOSSARY-10 and GLOSSARY-19 are the
 * authorities for the two identifier names: two identifiers, two names,
 * never a bare "code". The Pool ID is a plain read-only row, not a
 * `CopyField` (`plan.md ## 6.7`): it is never copied from here, and this
 * card's whole point is that the two rows read differently on sight.
 */
import {
  useJoinGrantGet,
  useJoinGrantRegenerate,
} from "@sportsmet/pool-sdk/private";
import {
  Button,
  confirmDestructive,
  CopyField,
  InlineBanner,
  Skeleton,
} from "@sportsmet/ui";
import type { ReactNode } from "react";

import {
  extractErrorCode,
  showApiError,
  showOfflineError,
} from "../../errors/apiErrorBus";
import {
  JOINING_COPY_INVITE_CODE_LABEL,
  JOINING_INVITE_CODE_LABEL,
  JOINING_INVITE_CODE_LOADING_LABEL,
  JOINING_INVITE_CODE_RETRY_LABEL,
  JOINING_INVITE_CODE_UNAVAILABLE,
  JOINING_POOL_ID_LABEL,
  JOINING_POOL_ID_NOTE,
  JOINING_REGENERATE_CONFIRM_BODY,
  JOINING_REGENERATE_CONFIRM_LABEL,
  JOINING_REGENERATE_CONFIRM_TITLE,
  JOINING_REGENERATE_LABEL,
} from "./joiningCardCopy";
import { PoolQrButton } from "../poolInvite/PoolQrButton";
import { resolveInviteCodeState, resolvePoolIdValue } from "./joiningCardState";

export type typePoolSettingsJoinCodeBlockProps = {
  poolId: string;
  poolRefId: string | null | undefined;
  /** Names the pool on the QR code's scan view (ISSUE-1339). */
  poolName: string;
};

export const PoolSettingsJoinCodeBlock = ({
  poolId,
  poolRefId,
  poolName,
}: typePoolSettingsJoinCodeBlockProps): ReactNode => {
  const grant = useJoinGrantGet({ poolId });
  const regenerate = useJoinGrantRegenerate();

  async function handleRegenerate(): Promise<void> {
    const isConfirmed = await confirmDestructive({
      title: JOINING_REGENERATE_CONFIRM_TITLE,
      body: JOINING_REGENERATE_CONFIRM_BODY,
      confirmLabel: JOINING_REGENERATE_CONFIRM_LABEL,
    });
    if (!isConfirmed) return;

    try {
      const result = await regenerate.mutateAsync({ poolId });
      showApiError(result);
    } catch {
      showOfflineError();
    }
  }

  const joinUrl = grant.data?.data?.joinUrl ?? null;
  const state = resolveInviteCodeState({
    isLoading: grant.isLoading,
    isTransportError: grant.isError,
    errorCode: extractErrorCode(grant.data),
    joinCode: grant.data?.data?.joinCode ?? null,
  });

  return (
    <div
      className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between"
      data-testid="pool-settings-join-code-block"
    >
      <div className="flex flex-col gap-2">
        {state.phase === "loading" ? (
          <div data-testid="pool-settings-invite-code-loading">
            <Skeleton
              shape="control"
              label={JOINING_INVITE_CODE_LOADING_LABEL}
            />
          </div>
        ) : null}
        {state.phase === "failed" ? (
          <div data-testid="pool-settings-invite-code-failed">
            <InlineBanner
              variant="warning"
              message={JOINING_INVITE_CODE_UNAVAILABLE}
              action={{
                label: JOINING_INVITE_CODE_RETRY_LABEL,
                onAction: () => {
                  void grant.refetch();
                },
              }}
            />
          </div>
        ) : null}
        {state.phase === "ready" ? (
          <>
            <CopyField
              label={JOINING_INVITE_CODE_LABEL}
              value={state.joinCode}
              copyLabel={JOINING_COPY_INVITE_CODE_LABEL}
              data-testid="pool-settings-invite-code"
            />
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  void handleRegenerate();
                }}
                loading={regenerate.isPending}
                loadingLabel={JOINING_REGENERATE_LABEL}
                data-testid="pool-settings-regenerate-invite-code"
              >
                {JOINING_REGENERATE_LABEL}
              </Button>
              {/* ISSUE-1339. The invite link as a QR code. */}
              {joinUrl === null ? null : (
                <PoolQrButton
                  inviteUrl={joinUrl}
                  poolName={poolName}
                  data-testid="pool-settings-qr"
                />
              )}
            </div>
          </>
        ) : null}
      </div>
      {/*
        `flex flex-col` on this wrapper only. The three children below are
        exactly the three the plan specifies, unchanged; without a block or
        flex context between the two `<span>`s they render on one inline
        line ("Pool IDC2RN28"), which a live browser pass caught. This class
        is the minimal fix, matching CopyField's own label-above-value
        reading (`## 5.19`'s "two rows read as one system").
      */}
      <div className="flex flex-col gap-1" data-testid="pool-settings-pool-id">
        <span className="text-caption text-ink-secondary">
          {JOINING_POOL_ID_LABEL}
        </span>
        <span className="text-body-strong text-ink break-all">
          {resolvePoolIdValue(poolRefId)}
        </span>
        <p className="text-caption text-ink-tertiary">{JOINING_POOL_ID_NOTE}</p>
      </div>
    </div>
  );
};
